import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import useResizeObserver from '../hooks/useResizeObserver';
import { addToCart } from '../utils/cart';
import { DbProduct } from '../api/db';
import { useCart } from '../contexts/CartContext';

// Define the product interface
export interface Product {
  id: string | number;
  name: string;
  brand: string;
  price: number;
  image: string;
  images?: string[]; // Add optional images array
  sizes: string[];
  collab?: string;
  condition?: string;
  size?: string;
  category?: string;
  sizeType?: string;
  sizeDisplay?: string;
  colorway?: string; // Add colorway property
  availableQuantity?: number; // Add available quantity
}

// Generate a fallback image based on product name
const getProductImageFallback = (productName: string): string => {
  const primaryColor = '#9333ea'; // Purple accent color
  const secondaryColor = '#0a0a0a'; // Background color
  
  const initials = productName
    .split(' ')
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
  
  return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300">
    <rect width="300" height="300" fill="${secondaryColor}" />
    <text x="50%" y="50%" dy=".1em" font-family="Arial" font-size="56" fill="${primaryColor}" text-anchor="middle" dominant-baseline="middle">${initials}</text>
    <text x="50%" y="75%" font-family="Arial" font-size="20" fill="white" text-anchor="middle">Product Image</text>
  </svg>`;
};

// Helper function to ensure images display correctly
const getImageUrl = (imageUrl?: string, productName?: string): string => {
  if (!imageUrl) {
    return getProductImageFallback(productName || 'Unknown');
  }
  
  // If it's a local blob URL and we're in production, use the fallback
  if (imageUrl.startsWith('blob:') && window.location.hostname !== 'localhost') {
    console.warn('Blob URL detected in production environment, using fallback for:', productName);
    return getProductImageFallback(productName || 'Unknown');
  }
  
  return imageUrl;
};

export const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [addedToCart, setAddedToCart] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const cardRef = useRef<HTMLDivElement>(null);
  const unmountingRef = useRef(false);
  const location = useLocation();
  const { addToCart } = useCart();
  
  // Get array of product images
  const productImages = product.images && product.images.length > 0 
    ? product.images 
    : product.image ? [product.image] : [];
  
  // Check if product has multiple images
  const hasMultipleImages = productImages.length > 1;
  
  // Debug logging for product images array
  useEffect(() => {
    // Intentionally left empty - debug logs removed
  }, [product.id, product.name, productImages, location]);
  
  // Log product data when component mounts
  useEffect(() => {
    // Intentionally left empty - debug logs removed
  }, [product.id, product.name, product.sizeDisplay, product.sizes]);
  
  // Handle image load error
  const handleImageError = () => {
    if (unmountingRef.current) return;
    setImageError(true);
  };

  // Handle component mounting/unmounting
  useEffect(() => {
    // Reset unmounting flag when component mounts
    unmountingRef.current = false;
    
    return () => {
      // Mark component as unmounting
      unmountingRef.current = true;
    };
  }, []);

  // Observer for entrance animations
  useEffect(() => {
    if (!cardRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && entry.isIntersecting && !unmountingRef.current) {
          setAnimateIn(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    try {
      observer.observe(cardRef.current);
    } catch (error) {
      console.error("IntersectionObserver error:", error);
      // Fallback if observer fails
      if (!unmountingRef.current) {
        setAnimateIn(true);
      }
    }

    return () => {
      try {
        observer.disconnect();
      } catch (error) {
        console.error("Error disconnecting observer:", error);
      }
    };
  }, []);
  
  // Handle mouse events
  const handleMouseEnter = () => {
    if (!unmountingRef.current) {
      setIsHovered(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (!unmountingRef.current) {
      setIsHovered(false);
    }
  };
  
  // Image navigation handlers
  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (productImages.length <= 1) return;
    setCurrentImageIndex(prev => 
      prev === 0 ? productImages.length - 1 : prev - 1
    );
  };
  
  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (productImages.length <= 1) return;
    setCurrentImageIndex(prev => 
      prev === productImages.length - 1 ? 0 : prev + 1
    );
  };
  
  // Handle add to cart
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setErrorMessage(null); // Clear any previous errors
    
    // Convert from FrontendProduct/Product to DbProduct format
    // required by the addToCart function
    const dbProduct: DbProduct = {
      id: product.id.toString(),
      brand: product.brand,
      type: product.name.replace(product.brand, '').trim(),
      colorway: product.colorway || '',
      size: product.size || product.sizeDisplay || product.sizes[0] || '',
      size_type: product.sizeType || '',
      condition: product.condition || 'New',
      price: product.price,
      og_box: false,
      collab: product.collab,
      created_at: new Date(),
      status: 'active',
      availableQuantity: product.availableQuantity || 1 // Add available quantity
    };
    
    // Get the first image URL
    const imageUrl = product.images && product.images.length > 0 
      ? product.images[0] 
      : product.image || '';
    
    // Add to cart using the context
    const result = addToCart(dbProduct, 1, imageUrl);
    
    // Check for error
    if (!result.success && result.message) {
      setErrorMessage(result.message);
      // Clear error after 3 seconds
      setTimeout(() => {
        setErrorMessage(null);
      }, 3000);
      return;
    }
    
    // Only show success message if the item was actually added
    if (result.success) {
      setAddedToCart(true);
      setTimeout(() => {
        setAddedToCart(false);
      }, 2000);
    }
  };
  
  return (
    <div 
      ref={cardRef}
      className={`relative group bg-background-light rounded-lg overflow-hidden shadow-md transition-all duration-500 hover:shadow-xl hover-lift ${
        animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Product image with add to cart overlay */}
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden bg-background-dark p-3">
          {errorMessage && (
            <div className="absolute top-0 inset-x-0 z-20 bg-red-900/90 border-b border-red-500 p-2 text-center">
              <p className="text-red-300 text-xs">{errorMessage}</p>
            </div>
          )}
          <div className="aspect-square overflow-hidden rounded-md relative">
            <img
              src={imageError 
                ? getProductImageFallback(product.name) 
                : getImageUrl(productImages[currentImageIndex] || product.image, product.name)}
              alt={product.name}
              onError={handleImageError}
              className={`object-contain w-full h-full transition-all duration-700 ease-in-out ${
                isHovered ? 'scale-110 filter brightness-90' : 'scale-100'
              }`}
            />
            
            {/* "Add to Cart" button overlay that appears on hover */}
            <div 
              className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity duration-300 ${
                isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <button
                onClick={handleAddToCart}
                className={`transform transition-all duration-300 px-4 py-2 rounded-md font-semibold ${
                  addedToCart 
                    ? 'bg-green-500 hover:bg-green-600 text-white scale-105' 
                    : 'bg-accent hover:bg-accent/90 text-white'
                }`}
              >
                {addedToCart ? (
                  <span className="flex items-center">
                    <FontAwesomeIcon icon={faCheck} className="mr-2" />
                    Added!
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FontAwesomeIcon icon={faShoppingCart} className="mr-2" />
                    Add to Cart
                  </span>
                )}
              </button>
            </div>
            
            {/* Image navigation arrows - always visible for products with multiple images */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-black/60 flex items-center justify-center text-white opacity-80 hover:opacity-100 transition-opacity z-10 shadow-md touch-manipulation"
                  aria-label="Previous image"
                >
                  <FiChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 rounded-full bg-black/60 flex items-center justify-center text-white opacity-80 hover:opacity-100 transition-opacity z-10 shadow-md touch-manipulation"
                  aria-label="Next image"
                >
                  <FiChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                </button>
                
                {/* Image indicator dots - always visible */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 mt-2">
                  {productImages.map((_, idx) => (
                    <div 
                      key={idx} 
                      className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${
                        idx === currentImageIndex ? 'bg-accent' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Brand and condition badges */}
          <div className="absolute top-5 left-5 px-2 py-1 bg-background/80 backdrop-blur-sm text-xs font-medium rounded-md text-white">
            {product.brand}
          </div>
          
          {product.condition && (
            <div className="absolute top-5 right-5 px-2 py-1 bg-accent/80 backdrop-blur-sm text-xs font-medium rounded-md text-white">
              {product.condition}
            </div>
          )}
        </div>
      </Link>
      
      {/* Product info */}
      <div className="p-4">
        <Link to={`/product/${product.id}`} className="block">
          <h3 className="text-sm sm:text-base font-semibold mb-1 text-primary line-clamp-2 hover:text-accent transition-colors">
            {product.name}
          </h3>
        </Link>
        
        <div className="flex justify-between items-center">
          <div>
            <p className="text-accent font-bold mt-1">
              ${product.price?.toFixed(2)}
            </p>
            {product.sizeDisplay && (
              <p className="text-xs text-secondary mt-1">
                Size: {product.sizeDisplay}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 